/**
 * Calculate the distance between two points on Earth using the Haversine formula
 * @param lat1 Latitude of the first point
 * @param lon1 Longitude of the first point
 * @param lat2 Latitude of the second point
 * @param lon2 Longitude of the second point
 * @returns Distance in meters
 */
export const calculateDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number => {
  const R = 6371000; // Earth's radius in meters
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Convert a distance value to meters
 * @param distance Distance value in the provided unit
 * @param unit Unit of measurement ('m' | 'ft' | 'km' | 'mi' | etc.)
 * @returns Distance in meters
 */
export const convertDistanceToMeters = (distance: number, unit: string = 'm'): number => {
  switch (unit) {
    case 'ft':
      return distance * 0.3048;
    case 'km':
      return distance * 1000;
    case 'mi':
      return distance * 1609.34;
    default:
      return distance;
  }
};

/**
 * Convert degrees to radians
 */
const toRadians = (degrees: number): number => degrees * (Math.PI / 180);

/**
 * Format distance for display
 * @param distanceInMeters Distance in meters
 * @param unit Target unit ('m' for meters, 'ft' for feet)
 * @param precision Number of decimal places for small distances
 * @returns Formatted distance string
 */
export const formatDistance = (
  distanceInMeters: number,
  unit: 'm' | 'ft' = 'm',
  precision: number = 1,
): string => {
  if (Number.isNaN(distanceInMeters) || distanceInMeters < 0) {
    return '–';
  }

  if (unit === 'ft') {
    const distanceInFeet = distanceInMeters * 3.28084;

    if (distanceInFeet < 1000) {
      return `${Math.round(distanceInFeet)} ft`;
    }

    const distanceInMiles = distanceInFeet / 5280;
    if (distanceInMiles < 10) {
      return `${distanceInMiles.toFixed(precision)} mi`;
    }

    return `${Math.round(distanceInMiles)} mi`;
  }

  // Metric units
  if (distanceInMeters < 1000) {
    return `${Math.round(distanceInMeters)} m`;
  }

  const distanceInKm = distanceInMeters / 1000;
  if (distanceInKm < 10) {
    return `${distanceInKm.toFixed(precision)} km`;
  }

  return `${Math.round(distanceInKm)} km`;
};
